import { useState } from 'react'

import { Select } from '@mantine/core'

import { ApplicantStage } from 'src/graphql/types/applicants'
import { formatSnakeValue } from 'src/lib/formatters'

import DisqualifiedReasonOptions from './DisqualifiedReasonOptions'
import HiringStageOptions from './HiringStageOptions'
import { inlineSelectProps } from './inlineSelectStyles'
import NotInterestedReasonOptions from './NotInterestedReasonOptions'
import TerminatedReasonOptions from './TerminatedReasonOptions'

const findDefaultSubStage = (applicantStage) => {
  switch (applicantStage?.applicantStage) {
    case ApplicantStage.PROSPECT:
      return { hiringStage: applicantStage?.hiringStage }
    case ApplicantStage.HIRED:
      return {}
    case ApplicantStage.DISQUALIFIED:
      return { disqualifiedReason: applicantStage?.disqualifiedReason }
    case ApplicantStage.NOT_INTERESTED:
      return { notInterestedReason: applicantStage?.notInterestedReason }
    case ApplicantStage.TERMINATED:
      return { terminatedReason: applicantStage?.terminatedReason }
    default:
      return {}
  }
}

const ApplicantStatusOptions = ({ applicantStage, setApplicantStage }) => {
  const applicantStageOptions = Object.values(ApplicantStage).map((stage) => ({
    value: stage,
    label: formatSnakeValue(stage),
  }))

  const [selectedApplicantStage, setSelectedApplicantStage] = useState(
    applicantStage?.applicantStage ?? null
  )

  // Depending on applicantStage.applicantStage, we will set defaultSubStage
  const defaultSubStage = findDefaultSubStage(applicantStage)

  const [selectedSubStage, setSelectedSubStage] = useState(defaultSubStage)

  const handleStageChange = (stage) => {
    if (!stage) return
    setSelectedApplicantStage(stage)
    setSelectedSubStage({})
    setApplicantStage({ applicantStage: stage })
  }

  const handleSubStageChange = (subStage) => {
    if (!selectedApplicantStage) return
    setApplicantStage({
      ...subStage,
      applicantStage: selectedApplicantStage,
    })
    setSelectedSubStage(subStage)
  }

  const renderSubStageOptions = () => {
    switch (selectedApplicantStage) {
      case ApplicantStage.PROSPECT:
        return (
          <HiringStageOptions
            selectedHiringStage={selectedSubStage}
            setSelectedHiringStage={handleSubStageChange}
          />
        )
      case ApplicantStage.DISQUALIFIED:
        return (
          <DisqualifiedReasonOptions
            selectedDisqualifiedReason={selectedSubStage}
            setSelectedDisqualifiedReason={handleSubStageChange}
          />
        )
      case ApplicantStage.NOT_INTERESTED:
        return (
          <NotInterestedReasonOptions
            selectedNotInterestedReason={selectedSubStage}
            setSelectedNotInterestedReason={handleSubStageChange}
          />
        )
      case ApplicantStage.TERMINATED:
        return (
          <TerminatedReasonOptions
            selectedTerminatedReason={selectedSubStage}
            setSelectedTerminatedReason={handleSubStageChange}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col gap-3 text-xs">
      <Select
        {...inlineSelectProps}
        value={selectedApplicantStage}
        onChange={handleStageChange}
        data={applicantStageOptions}
        placeholder="Select stage"
        className="max-w-sm"
        searchable
      />

      {renderSubStageOptions()}
    </div>
  )
}

export default ApplicantStatusOptions
